import { agaveService } from 'app/services/agave.service';
import { ERPPostingStatus, HighnoteTransactionEvent, AgaveAPInvoicesCreateExpenseInput } from 'API';
import { PostingContext, Subscription } from './types';
import { createPostingSubscription } from './sharedPostingUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { formatAmountForAgavePost, stripPaidolPrefix } from 'app/services/agave/erpUtils';

/**
 * Builds Vista AP Invoice payload for queue-based posting.
 * Uses /ap-invoices with line_items array.
 */
export function buildVistaExpensePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccountToken: string
): AgaveAPInvoicesCreateExpenseInput {
  const paymentNumber = transaction.id.substring(0, 15);
  const issueDate = new Date(transaction.createdAt).toISOString().split('T')[0];
  // Vista limits the AP Invoice Description to 30 chars. Send only the note
  // (the merchant/vendor is already identified elsewhere on the invoice) and
  // truncate to fit, so notes no longer overflow the limit (SEE-1100).
  const VISTA_DESCRIPTION_MAX_LENGTH = 30;
  const description = (transaction.note ?? '').substring(0, VISTA_DESCRIPTION_MAX_LENGTH);
  const { amount } = formatAmountForAgavePost(transaction.approvedAmount?.value ?? 0, transaction.status);

  const expense: AgaveAPInvoicesCreateExpenseInput = {
    account_token: agaveAccountToken,
    erp: AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED,
    description,
    merchantName: 'Speedchain',
    number: paymentNumber,
    issue_date: issueDate,
    line_items: [
      {
        amount,
        quantity: '1.0',
        type: 'Job',
        unit_of_measure: 'EA',
        ...(transaction.agaveVendorId && { vendor_id: stripPaidolPrefix(transaction.agaveVendorId) }),
        ...(transaction.agaveProjectId && { project_id: stripPaidolPrefix(transaction.agaveProjectId) }),
        ...(transaction.agaveCostCodeId && { cost_code_id: stripPaidolPrefix(transaction.agaveCostCodeId) }),
        ...(transaction.agaveCostTypeId && { cost_type_id: stripPaidolPrefix(transaction.agaveCostTypeId) }),
        ...(transaction.agaveGlCodeId && { ledger_account_id: stripPaidolPrefix(transaction.agaveGlCodeId) }),
      },
    ],
  };

  return expense;
}

export interface VistaPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccountToken: string;
}

/**
 * Handles Vista posting via queue
 */
export async function postVistaTransaction(
  params: VistaPostingParams,
  context: PostingContext
): Promise<void> {
  const { transactionId, paidolId, transaction: txn, agaveAccountToken } = params;
  const { dispatch, enqueueSnackbar, t, setIsAgaveLoading, setTransactionPosting, captureException } =
    context;

  setIsAgaveLoading(true);

  // Declare subscription outside try block so it can be cleaned up in catch
  let subscription: Subscription | undefined;

  try {
    // Build posting data
    const postingData = buildVistaExpensePayload(txn, agaveAccountToken);
    const postingDataString = JSON.stringify(postingData);

    // Set up subscription BEFORE starting the async process
    // This ensures we catch status updates even if posting completes very quickly
    subscription = createPostingSubscription(transactionId, context);

    // Start the async Vista posting via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startVistaPosting({
      input: {
        transactionId,
        paidolId,
        erp: AgaveSourceSystem.VIEWPOINT_VISTA_HOSTED,
        postingType: 'AP_INVOICE',
        postingData: postingDataString,
      },
    });

    // Check if mutation succeeded and status is PENDING
    if (result?.status === ERPPostingStatus.PENDING) {
      enqueueSnackbar(t('submitExpense.postingExpense'), { variant: 'info' });
    } else {
      subscription.unsubscribe();
      // If status is not PENDING, it might be FAILED - check the response
      const responseStatus = result?.status;
      const errorMessage = result?.error || 'Failed to start Vista posting';

      // Update Redux to clear posting state immediately
      const { applyLocalTransactionUpdates } = context;
      dispatch(
        applyLocalTransactionUpdates([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: responseStatus || null,
              erpPostingError: errorMessage,
            },
          },
        ]) as any
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    // Clean up subscription to prevent memory leak
    subscription?.unsubscribe();

    // IMPORTANT: Clear posting state FIRST to prevent UI from showing "posting" status
    // This handles GraphQL validation errors that occur before the mutation reaches the backend
    setTransactionPosting({ id: transactionId, isPosting: false });
    setIsAgaveLoading(false);

    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure transaction status is updated in Redux
    const { applyLocalTransactionUpdates } = context;
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null, // Clear posting status on error
            erpPostingError: errorMessage,
          },
        },
      ]) as any
    );
  }
}
